import React, { useState, useMemo } from 'react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { SmartCalendar } from '@/apps/master-plan/shared/components/SmartCalendar';
import { toLogicDate } from '@/utils/date-utils';
import { TacticalEmblaWheel } from './TacticalEmblaWheel';
import { HorizontalDateScroller } from './HorizontalDateScroller';
import { Calendar as CalendarIcon, List } from 'lucide-react';

interface UnifiedTemporalPickerProps {
    date: string;
    time: string;
    duration?: number;
    mode?: 'START' | 'END' | 'DURATION';
    label: string;
    onUpdate: (updates: { date?: string, time?: string, duration?: number }) => void;
    onClose: () => void;
}

/**
 * UnifiedTemporalPicker: Combines SmartCalendar and Tactical Embla Wheels.
 * V5.1: Added originDate reference and hardened scroller layout.
 */
export const UnifiedTemporalPicker: React.FC<UnifiedTemporalPickerProps> = ({ 
    date, time, duration = 0, mode = 'START', label, onUpdate, onClose 
}) => {
    // Capture the initial state when the picker was opened
    const [originDate] = useState(date);

    const [localDate, setLocalDate] = useState(date);
    const [localTime, setLocalTime] = useState(time);
    const [localDuration, setLocalDuration] = useState(duration);
    const [dateMode, setDateMode] = useState<'GRID' | 'SCROLLER'>('GRID');
    
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date(date));
    
    const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')), []);
    const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')), []);
    
    const durHours = useMemo(() => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')), []);
    const durMins = useMemo(() => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')), []);

    const h = localTime.split(':')[0];
    const m = localTime.split(':')[1];

    const dH = Math.floor(localDuration / 60).toString().padStart(2, '0');
    const dM = (localDuration % 60).toString().padStart(2, '0');

    const parseDate = (dStr: string) => {
        const [y, mon, day] = dStr.split('-').map(Number);
        return new Date(y, mon - 1, day);
    };

    const handleDone = () => {
        onUpdate({
            date: localDate,
            time: localTime,
            duration: localDuration
        });
        onClose();
    };

    return (
        <div style={{ 
            backgroundColor: '#111', 
            borderRadius: '12px', 
            border: '1px solid #333', 
            padding: '20px', 
            width: '100%', 
            maxWidth: '400px',
            boxShadow: '0 0 100px rgba(0,0,0,1)', // Deep shadow
            zIndex: 10002,
            position: 'relative'
        }}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <Typography style={{ fontSize: 11, fontWeight: '950', color: '#f29b11', letterSpacing: 2 }}>{label}</Typography>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                    {mode !== 'DURATION' && (
                        <Pressable 
                            onPress={() => setDateMode(dateMode === 'GRID' ? 'SCROLLER' : 'GRID')} 
                            style={{ padding: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 6 }}
                        >
                            {dateMode === 'GRID' ? <List size={14} color="#f29b11" /> : <CalendarIcon size={14} color="#f29b11" />}
                        </Pressable>
                    )}
                    <Pressable onPress={onClose} style={{ padding: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 6 }}>
                        <Typography style={{ fontSize: 10, fontWeight: '950', color: '#666' }}>CANCEL</Typography>
                    </Pressable>
                    <Pressable onPress={handleDone} style={{ padding: 8, backgroundColor: 'rgba(242, 155, 17, 0.1)', borderRadius: 6, border: '1px solid rgba(242, 155, 17, 0.2)' }}>
                        <Typography style={{ fontSize: 10, fontWeight: '950', color: '#f29b11' }}>DONE</Typography>
                    </Pressable>
                </div>
            </div>

            {mode !== 'DURATION' && (
                <div style={{ marginBottom: '20px' }}>
                    {dateMode === 'GRID' ? (
                        <SmartCalendar 
                            currentMonth={currentMonth}
                            onMonthChange={setCurrentMonth}
                            selectedDate={parseDate(localDate)}
                            onSelectDate={(d) => setLocalDate(toLogicDate(d))}
                            getDateStyle={(d) => {
                                // Add origin marker to the calendar grid
                                if (toLogicDate(d) === originDate && toLogicDate(d) !== localDate) {
                                    return { color: '#f29b11', fontWeight: '900' };
                                }
                                return {};
                            }}
                        />
                    ) : (
                        <HorizontalDateScroller 
                            selectedValue={localDate}
                            originDate={originDate}
                            onSelect={setLocalDate}
                        />
                    )}
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                {mode === 'DURATION' ? (
                    <>
                        <TacticalEmblaWheel 
                            label="HOURS"
                            data={durHours}
                            value={dH}
                            onSelect={(val) => setLocalDuration(parseInt(val) * 60 + parseInt(dM))}
                        />
                        <TacticalEmblaWheel 
                            label="MINUTES"
                            data={durMins}
                            value={dM}
                            onSelect={(val) => setLocalDuration(parseInt(dH) * 60 + parseInt(val))}
                        />
                    </>
                ) : (
                    <>
                        <TacticalEmblaWheel 
                            label="HOUR"
                            data={hours}
                            value={h}
                            onSelect={(val) => setLocalTime(`${val}:${m}`)}
                        />
                        <TacticalEmblaWheel 
                            label="MIN"
                            data={minutes}
                            value={m}
                            onSelect={(val) => setLocalTime(`${h}:${val}`)}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
